import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Clock, MessageCircle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { rp, fmtDateTime, waLink } from "@/lib/format";
import { useSeo } from "@/lib/shop";
import { Loading, ErrorState } from "@/components/site/States";
import { OrderStatusBadge, PaymentBadge } from "@/components/site/Badges";
import { Countdown, ProofForm, QrisPanel } from "@/pages/Nota";

function StatusBox({ n }) {
  const pending = n.payments.some((p) => p.status === "pending");
  const text = n.payment_status === "lunas" ? "Pembayaran sudah diverifikasi admin. Pesanan diproses."
    : pending ? "Bukti pembayaran terkirim dan menunggu verifikasi admin."
    : n.status === "dibatalkan" ? "Pesanan ini telah dibatalkan."
    : "Menunggu pembayaran. Scan QRIS lalu unggah bukti pembayaran.";
  const tone = n.payment_status === "lunas" ? "bg-emerald-50 border-emerald-300 text-emerald-900" : n.status === "dibatalkan" ? "bg-red-50 border-red-200 text-red-800" : pending ? "bg-sky-50 border-sky-200 text-sky-900" : "bg-amber-50 border-amber-200 text-amber-900";
  return <p className={`rounded-xl border p-3 text-sm font-semibold ${tone}`} data-testid="pay-status-text">{text}</p>;
}

export default function QrisPay() {
  const { no } = useParams();
  const [sp] = useSearchParams();
  const t = sp.get("t") || "";
  const [n, setN] = useState(null);
  const [err, setErr] = useState("");
  useSeo(`Bayar QRIS ${no}`, "Pembayaran QRIS Bengkel Toys & Hobbies");
  useEffect(() => {
    api.get(`/nota/${no}`, { params: { t } }).then((r) => setN(r.data)).catch((e) => setErr(errMsg(e)));
  }, [no, t]);
  if (err) return <div className="container-x py-16"><ErrorState text={err} /></div>;
  if (!n) return <Loading />;
  if (!n.qris?.enabled) {
    return (
      <div className="container-x py-16 max-w-xl text-center" data-testid="qris-unavailable">
        <p className="font-semibold mb-4">Pembayaran QRIS sedang tidak tersedia. Silakan bayar lewat transfer bank di halaman nota.</p>
        <Link to={`/nota/${n.order_no}?t=${t}`} className="btn-ink">Buka Nota & Transfer Bank</Link>
      </div>
    );
  }
  const closed = n.status === "dibatalkan" || n.payment_status === "lunas";
  const pending = n.payments.some((p) => p.status === "pending");
  const notaLink = `/nota/${n.order_no}?t=${t}`;
  const waMsg = `Halo ${n.store.store_name}, saya sudah bayar via QRIS untuk pesanan *${n.order_no}* sebesar ${rp(n.amount_due)} atas nama ${n.customer.name}. Bukti sudah saya unggah: ${window.location.origin}${notaLink}`;
  return (
    <div className="container-x py-6 sm:py-10 max-w-4xl" data-testid="qris-pay-page">
      <Link to={notaLink} className="inline-flex items-center gap-1 text-sm font-semibold underline mb-4" data-testid="qris-back-to-order"><ArrowLeft className="h-4 w-4" />Kembali ke detail pesanan</Link>
      <div className="bg-[var(--bk-yellow)] border-2 border-neutral-900 rounded-2xl p-4 sm:p-6 mb-5 flex flex-wrap items-center gap-4 justify-between">
        <div className="flex items-center gap-3">
          <img src="/brand/logo-sm.webp" alt="Bengkel Toys & Hobbies" className="h-14 w-auto" />
          <div>
            <div className="text-xs font-bold uppercase">Pembayaran QRIS</div>
            <div className="font-mono text-xl font-bold" data-testid="qris-order-no">{n.order_no}</div>
            <div className="text-xs">{fmtDateTime(n.created_at)}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs font-bold">{n.kind === "po" && n.paid_amount < n.dp_total ? "DP yang dibayar" : "Total pembayaran"}</div>
          <div className="price text-3xl font-extrabold" data-testid="qris-total">{rp(n.amount_due)}</div>
          <div className="flex gap-1 justify-end mt-1"><OrderStatusBadge status={n.status} /><span data-testid="qris-payment-status"><PaymentBadge status={n.payment_status} /></span></div>
        </div>
      </div>
      <div className="space-y-5">
        <StatusBox n={n} />
        {!closed && n.pay_deadline && n.paid_amount === 0 && !pending && <p className="text-sm flex items-center gap-1"><Clock className="h-4 w-4" /><Countdown to={n.pay_deadline} /></p>}
        {!closed && n.qris?.enabled && <QrisPanel q={n.qris} amount={n.amount_due} />}
        {n.fulfillment === "delivery" && !closed && <p className="text-xs">Ongkos kirim belum termasuk dan akan ditagih terpisah setelah dikonfirmasi admin.</p>}
        {!closed && !pending && <ProofForm n={n} no={no} t={t} onDone={setN} qris />}
        <div className="flex flex-col sm:flex-row gap-2">
          <a href={waLink(n.wa_target || n.store.whatsapp, waMsg)} target="_blank" rel="noreferrer" className="btn-wa sm:flex-1" data-testid="qris-confirm-wa"><MessageCircle className="h-4 w-4" />Konfirmasi ke WhatsApp</a>
          <Link to={notaLink} className="btn-brand sm:flex-1" data-testid="qris-nota-link">Lihat Nota / Detail Pesanan</Link>
        </div>
      </div>
    </div>
  );
}
